/* eslint-disable no-console */
import { useEffect, useState } from "react";
import { useAuthContext } from "../../context/auth";
import { getUsers, getIntermediarios } from "../../utils";
import "./stylesListaClientesPorEjecutivo.scss";

export const ListaClientesPorEjecutivo = ({ clientes = [], categorias = [], filtroCategoria, filtroCliente }) => {
  const [usuariosEjecutivos, setUsuariosEjecutivos] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);

  const { pais } = useAuthContext();

  const getPaisCode = (countryName) => {
    switch (countryName.toLowerCase()) {
      case "uruguay":
        return "URU";
      case "chile":
        return "CHI";
      case "perú":
        return "PER";
      default:
        return countryName;
    }
  };

  const getCategoriaNombre = (categoriaId) => {
    const categoria = categorias.find((cat) => cat.id === Number(categoriaId));
    return categoria ? categoria.nombre : "Sin Categoría";
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const paisCode = getPaisCode(pais);
        const [allUsers, intermediariosData] = await Promise.all([getUsers({}), getIntermediarios({ pais })]);

        // Filter executives by role and country, then exclude those without clients
        const ejecutivosConClientes = new Set(clientes.map((c) => String(c.ejecutivo)));

        const filteredEjecutivos = (allUsers || [])
          .filter(
            (user) =>
              ["comercial", "gerencia"].includes(user.rol) &&
              getPaisCode(user.pais) === paisCode &&
              ejecutivosConClientes.has(String(user.id))
          )
          .sort((a, b) => {
            if (a.rol === "comercial" && b.rol === "gerencia") return -1;
            if (a.rol === "gerencia" && b.rol === "comercial") return 1;
            return 0;
          });
        setUsuariosEjecutivos(filteredEjecutivos);
        setIntermediarios(intermediariosData || []);
      } catch (error) {
        console.error("Error initializing:", error);
      }
    };

    if (clientes.length > 0) {
      initialize();
    }
  }, [pais, clientes]);

  const aplicaFiltros = (cliente) =>
    (!filtroCategoria || String(cliente.categoria) === String(filtroCategoria)) &&
    (!filtroCliente || String(cliente.id) === String(filtroCliente));

  const getClientesPorEjecutivo = (ejecutivoId) =>
    clientes.filter((cliente) => String(cliente.ejecutivo) === String(ejecutivoId) && aplicaFiltros(cliente));

  const getClientesPorBroker = (brokerId) =>
    clientes.filter((cliente) => String(cliente.broker_id) === String(brokerId) && aplicaFiltros(cliente));

  // Brokers activos: derivados de clientes con broker_id (segunda capa de intermediación).
  // Dinámico: cualquier intermediario con clientes asignados genera su columna, sin tocar código.
  const brokersActivos = [...new Set(clientes.map((c) => c.broker_id).filter((id) => id != null))]
    .map((id) => {
      const inter = intermediarios.find((i) => Number(i.id) === Number(id));
      return inter ? { id: Number(id), nombre: inter.nombre } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  // Modelo unificado de columnas: ejecutivos primero, luego brokers.
  const columnas = [
    ...usuariosEjecutivos.map((ej) => ({
      key: `ej-${ej.id}`,
      nombre: ej.nombre,
      getClientes: () => getClientesPorEjecutivo(ej.id),
    })),
    ...brokersActivos.map((br) => ({
      key: `br-${br.id}`,
      nombre: br.nombre,
      getClientes: () => getClientesPorBroker(br.id),
    })),
  ];

  if (columnas.length === 0) {
    return <div>No hay ejecutivos disponibles para {pais}</div>;
  }

  const columnWidth = `${100 / columnas.length}%`;

  return (
    <div className="cartera-table-container">
      <table className="table table-bordered table-hover table-striped" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            {columnas.map((col) => {
              const clientCount = col.getClientes().length;
              return (
                <th key={col.key} className="text-center cartera-th" style={{ width: columnWidth }}>
                  <div>{col.nombre}</div>
                  <small>
                    {clientCount === 0
                      ? "Sin Clientes"
                      : `${clientCount} ${clientCount === 1 ? "cliente" : "clientes"}`}
                  </small>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {(() => {
            const maxClients = Math.max(...columnas.map((col) => col.getClientes().length), 1);

            const rows = [];
            for (let i = 0; i < maxClients; i++) {
              rows.push(
                <tr key={i}>
                  {columnas.map((col) => {
                    const clientesColumna = col.getClientes();
                    return (
                      <td key={col.key} className="text-start">
                        {clientesColumna[i] ? (
                          <div>
                            <div>{clientesColumna[i].nombre}</div>
                            {!filtroCategoria && (
                              <small className="text-muted">{getCategoriaNombre(clientesColumna[i].categoria)}</small>
                            )}
                          </div>
                        ) : i === 0 && clientesColumna.length === 0 ? (
                          <span className="text-muted">-</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            }
            return rows;
          })()}
        </tbody>
      </table>
    </div>
  );
};
